import { useState, useEffect, useRef } from 'react'
import { sanitizeHtmlContent } from '@/utils/components'

const ZENDESK_ARTICLE_BASE_URL =
  'https://optiwatt.zendesk.com/api/v2/help_center/articles'
const DEFAULT_ARTICLE_ID = '47689571763995'

type ZendeskArticleState = {
  title: string | null
  bodyHtml: string | null
  isLoading: boolean
  error: string | null
}

function fetchZendeskArticle(
  articleId: string,
  signal: AbortSignal,
): Promise<{ title: string; body: string }> {
  return fetch(`${ZENDESK_ARTICLE_BASE_URL}/${articleId}.json`, { signal })
    .then((res) => {
      if (!res.ok) throw new Error(`Zendesk API error: ${res.status}`)
      return res.json()
    })
    .then((data) => ({
      title: data.article.title as string,
      body: data.article.body as string,
    }))
}

export default function useZendeskArticle(
  articleId: string = DEFAULT_ARTICLE_ID,
): ZendeskArticleState {
  const [state, setState] = useState<ZendeskArticleState>({
    title: null,
    bodyHtml: null,
    isLoading: true,
    error: null,
  })
  const cachedRef = useRef<{ id: string; title: string; bodyHtml: string }>()

  useEffect(
    function loadArticle() {
      if (cachedRef.current?.id === articleId) {
        setState({
          title: cachedRef.current.title,
          bodyHtml: cachedRef.current.bodyHtml,
          isLoading: false,
          error: null,
        })
        return
      }

      const controller = new AbortController()
      setState((prev) => ({ ...prev, isLoading: true, error: null }))

      fetchZendeskArticle(articleId, controller.signal)
        .then(({ title, body }) => {
          const sanitizedBody = sanitizeHtmlContent(body)
          cachedRef.current = { id: articleId, title, bodyHtml: sanitizedBody }
          setState({
            title,
            bodyHtml: sanitizedBody,
            isLoading: false,
            error: null,
          })
        })
        .catch((err) => {
          if (err.name === 'AbortError') return
          setState({
            title: null,
            bodyHtml: null,
            isLoading: false,
            error: err.message ?? 'Failed to load article',
          })
        })

      return () => controller.abort()
    },
    [articleId],
  )

  return state
}
